/**
 * MCP content envelope unwrapping.
 *
 * MCP tools return results wrapped in a content envelope:
 *   {type: "text", text: "<json>"}
 *
 * The backend's _encode_tool_output() serializes this as a JSON string
 * sent in the tool_end WebSocket event. This module handles the
 * double-parse: JSON string → envelope object → inner JSON payload.
 *
 * Single source of truth — replaces inline parsing previously duplicated in
 * useAssistantConnection.ts (tool_end handler + history_added function_call handler).
 */

/**
 * Parse and unwrap an MCP tool output string into the tool-specific payload.
 *
 * Handles three formats:
 *   1. MCP envelope: '{"type":"text","text":"{\\"data\\":[...]}"}' → inner parsed JSON
 *   2. Direct JSON: '{"data":[...]}' → parsed JSON object
 *   3. Plain string: 'some text' → string as-is
 *
 * @param raw - The raw output string from a tool_end WebSocket event
 * @returns The parsed payload (object, array, or string)
 */
export function unwrapMcpOutput(raw: string): unknown {
  let outer: unknown;
  try {
    outer = JSON.parse(raw);
  } catch {
    // Not JSON — return the raw string
    return raw;
  }

  // Check for MCP content envelope: {type: "text", text: "<json>"}.
  // Require BOTH type === 'text' AND a string `text` — keying on `text`
  // alone would mis-unwrap a direct tool payload that legitimately has a
  // top-level `text` field (discarding the rest of the object).
  if (
    outer &&
    typeof outer === 'object' &&
    !Array.isArray(outer) &&
    (outer as Record<string, unknown>).type === 'text' &&
    typeof (outer as Record<string, unknown>).text === 'string'
  ) {
    const innerText = (outer as Record<string, unknown>).text as string;
    try {
      return JSON.parse(innerText);
    } catch {
      // Inner text isn't JSON — return it as a string
      return innerText;
    }
  }

  // Already a plain JSON object/array — return directly
  return outer;
}
